import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Menu, X, ChevronDown } from 'lucide-react';
import WhatsAppIcon from './WhatsAppIcon';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/Header.css';

const Header = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close "More" dropdown when clicking outside or on route change
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMoreOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: t('nav.customTrip'), path: '/custom-trip' },
    { name: t('nav.packages'), path: '/experiences' },
    { name: t('nav.dailyTours'), path: '/services' },
    { name: t('nav.blog'), path: '/blog' },
  ];

  const secondaryNavItems = [
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.mission'), path: '/mission' },
  ];

  return (
    <header className={`header-modern ${isScrolled ? 'scrolled' : ''}`}>
      {/* Top Bar with Contact Info */}
      <div className="header-top-bar">
        <div className="header-top-container">
          <div className="header-contact-info">
            <a href="mailto:bookings@madeiraoutdoors.com" className="header-contact-item">
              <Mail size={16} />
              <span>bookings@madeiraoutdoors.com</span>
            </a>
            <a
              href="https://wa.me/351968656613"
              target="_blank"
              rel="noopener noreferrer"
              className="header-contact-item"
              data-testid="header-phone-whatsapp"
            >
              <WhatsAppIcon size={16} />
              <span>WhatsApp · +351 968 656 613</span>
            </a>
          </div>
          <div className="header-top-actions">
            <LanguageSelector />
            <a 
              href="https://wa.me/351968656613" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="header-whatsapp-link"
            >
              <WhatsAppIcon size={18} />
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="header-main-bar">
        <div className="header-main-container">
          {/* Logo */}
          <Link to="/" className="logo-link-modern">
            <img 
              src="https://customer-assets.emergentagent.com/job_15838cb8-d5c8-4088-b574-f4de08159ff9/artifacts/rfx6tebm_Copie%20de%20Copie%20de%20Madeira%20outdoors-5.png" 
              alt="Madeira Outdoors" 
              className="logo-modern"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav-desktop-modern">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link-modern ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.name}
              </Link>
            ))}

            {/* Secondary "More" dropdown for About + Mission */}
            <div className="nav-more-wrapper" ref={moreRef}>
              <button
                type="button"
                className={`nav-link-modern nav-more-toggle ${
                  secondaryNavItems.some((i) => i.path === location.pathname) ? 'active' : ''
                }`}
                onClick={() => setIsMoreOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={isMoreOpen}
                data-testid="header-more-toggle"
              >
                {t('nav.more')}
                <ChevronDown
                  size={16}
                  className={`nav-more-chevron ${isMoreOpen ? 'open' : ''}`}
                />
              </button>
              {isMoreOpen && (
                <div className="nav-more-dropdown" data-testid="header-more-dropdown">
                  {secondaryNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-more-item ${
                        location.pathname === item.path ? 'active' : ''
                      }`}
                      data-testid={`header-more-item-${item.path.replace('/', '')}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <Link to="/contact" className="header-cta-button">
            {t('header.cta')}
          </Link>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn-modern"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="nav-mobile-modern">
          <div className="nav-mobile-contact">
            <a href="mailto:bookings@madeiraoutdoors.com" className="nav-mobile-contact-item">
              <Mail size={16} />
              <span>bookings@madeiraoutdoors.com</span>
            </a>
            <a
              href="https://wa.me/351968656613"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-mobile-contact-item"
            >
              <WhatsAppIcon size={16} />
              <span>WhatsApp · +351 968 656 613</span>
            </a>
          </div>
          <nav className="nav-mobile-links">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link-mobile-modern ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="nav-mobile-divider">
              <span>{t('nav.more')}</span>
            </div>
            {secondaryNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link-mobile-modern secondary ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.path.replace('/', '')}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="nav-mobile-actions">
            <a 
              href="https://wa.me/351968656613" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-mobile-whatsapp"
            >
              <WhatsAppIcon size={18} />
              <span>{t('header.whatsapp')}</span>
            </a>
            <Link to="/contact" className="nav-mobile-cta">
              {t('header.cta')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
